import { TLearnerListOutputDto } from "@/api/services/main/learner/dtos/get-learner-list.dto";
import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { divider, icon } from "@/theme/colors";
import { calculateResidencePeriods } from "@/features/learner/utils/calculates";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { IconCheckCircleFilled } from "@/assets/icons";
import { Box, Stack } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LearnerPlanStatusCell } from "@/features/learner/components/learner-list/LearnerPlanStatusCell";
import { findLabelByValue } from "@/utils/object";
import { useEnum } from "@/api/services/global/enum";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";

type TLearnerListTableProps = {
  rows: TLearnerListOutputDto[];
  isLoading: boolean;
};

export const LearnerListTable: FC<TLearnerListTableProps> = ({ rows, isLoading }) => {
  const navigate = useNavigate();
  const { data: genderOptions } = useEnum("EGender");
  const { data: japaneseLevelTypeOptions } = useEnum("EJapaneseLevelType");
  const { data: planStatusOptions } = useEnum("EPlanStatus");
  const { userType } = useInitSetup();
  const isRsoUser = userType === USER_TYPE.RSO_USER;
  const isSupportUser = userType === USER_TYPE.SUPPORT_USER;

  const toDetailRoute = (learnerId: number) => {
    const route = getDynamicRoute(AppRoutes.learnerDetail, { learnerId: learnerId });
    navigate(route);
  };

  const truncateText = (value: string | null | undefined) => {
    if (!value) return EMPTY_STR.TEXT;
    return truncateWithEllipsis(value, 15);
  };

  const columns = useMemo<MRT_ColumnDef<TLearnerListOutputDto>[]>(
    () => [
      {
        id: "planStatus",
        header: "ステータス",
        accessorKey: "learnerPlan.planStatus",
        size: 134,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          const planStatus = row.original?.activeLearnerPlan?.planStatus ?? "";
          const options = (planStatusOptions ?? []).map((o) => ({ value: o.value, label: o.label }));
          return isSupportUser && !planStatus ? (
            EMPTY_STR.TEXT
          ) : (
            <LearnerPlanStatusCell learnerId={row.original.id} value={planStatus} options={options} isDisabledHandling={isSupportUser} />
          );
        },
        Header: <TableSorting name="activeLearnerPlan.planStatus">ステータス</TableSorting>,
      },
      {
        id: "fullName",
        header: "氏名",
        accessorKey: "fullName",
        size: 164,
        Cell: ({ row }) => truncateText(row.original?.fullName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="learner.fullName">氏名</TableSorting>,
      },
      ...(isRsoUser || isSupportUser
        ? [
            {
              id: "companyName",
              header: "所属機関",
              accessorKey: "companyName",
              size: 158,
              Cell: ({ row }: { row: { original: TLearnerListOutputDto } }) => truncateText(row.original?.rsoCompany?.companyName) ?? EMPTY_STR.TEXT,
              Header: <TableSorting name="rsoCompany.companyName">所属機関</TableSorting>,
            },
          ]
        : []),
      {
        id: "industry",
        header: "分野",
        accessorKey: "industry",
        size: 92,
        Cell: ({ row }) => truncateText(row.original?.industry?.name) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="industry.name">受験分野</TableSorting>,
      },
      {
        id: "nationality",
        header: "国籍",
        accessorKey: "nationality",
        size: 116,
        Cell: ({ row }) => truncateText(row.original?.nationality?.nationalityName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="nationality.nationalityName">国籍</TableSorting>,
      },
      {
        id: "gender",
        header: "性別",
        accessorKey: "gender",
        size: 60,
        Cell: ({ row }) => findLabelByValue(genderOptions, row.original?.gender.toString()),
      },
      {
        id: "prefecture",
        header: "居住地",
        accessorKey: "prefecture",
        size: 106,
        Cell: ({ row }) => truncateText(row.original?.prefecture) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="learner.prefecture">居住地</TableSorting>,
      },
      {
        id: "japaneseLanguageLevel",
        header: "日本語能力",
        accessorKey: "japaneseLanguageLevel",
        size: 136,
        Cell: ({ row }) =>
          row.original?.japaneseLanguageLevel ? findLabelByValue(japaneseLevelTypeOptions, row.original.japaneseLanguageLevel) : EMPTY_STR.TEXT,
        Header: <TableSorting name="learner.japaneseLanguageLevel">日本語能力</TableSorting>,
      },
      {
        id: "lastExamScore",
        header: "前回点数",
        accessorKey: "lastExamScore",
        size: 120,
        Cell: ({ row }) => (row.original?.lastExamScore != null ? `${row.original.lastExamScore}点` : EMPTY_STR.TEXT),
        Header: <TableSorting name="learner.lastExamScore">前回点数</TableSorting>,
      },
      {
        id: "remainingPeriod",
        header: "1号残期間",
        accessorKey: "remainingPeriod",
        size: 129,
        Cell: ({ row }) => calculateResidencePeriods(row.original?.clause1StartDate, row.original?.blankPeriodMonths, EMPTY_STR.TEXT).remaining,
        Header: <TableSorting name="remainingPeriod">1号残期間</TableSorting>,
      },
      {
        id: "accountStatus",
        header: "アカウント",
        accessorKey: "isAccountActive",
        size: 160,
        Cell: ({ row }) => {
          const isActive = row.original?.isAccountActive;
          return (
            <Stack direction="row" alignItems="center" spacing={1} gap={1}>
              <IconCheckCircleFilled
                style={{
                  color: isActive ? icon.primary : icon.disable,
                  fontSize: 24,
                }}
              />
              {isActive ? "発行済み" : "未発行"}
            </Stack>
          );
        },
        Header: <TableSorting name="isAccountActive">アカウント</TableSorting>,
      },
    ],
    [isRsoUser, isSupportUser, planStatusOptions, genderOptions, japaneseLevelTypeOptions],
  );

  const [heightTable] = useState<string>("calc(100vh - 565px)");

  const { table } = useTable({
    columns,
    rows,
    getRowId: (row) => row.id.toString(),
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    onClickRow: (row) => toDetailRoute(row.id),
    renderEmptyRowsFallback: ({}) => <Box></Box>,
  });

  return <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} headRowHeight={48} borderColor={divider.high} />;
};
